import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Observable, Subject, combineLatest, map } from 'rxjs';

import { whenTruthy } from 'ish-core/utils/operators';

import { DotCmsAnimationComponent } from '../../animations/animation/dot-cms-animation.component';
import { getFieldAnimation } from '../../animations/dot-cms-aniamtion.helper';
import { DotCmsUveFacade } from '../../facades/dot-cms-uve.facade';
import { ImageContentlet } from '../../models/dot-cms-uve-contentlet.model';
import { BlockEditorContent, BlockEditorNode } from '../../models/dotcms-block-editor.model';
import { DotCmsCodecEncodePipe } from '../../pipes/dot-cms-codec-encode.pipe';

@Component({
  selector: 'custom-dot-cms-image',
  templateUrl: './dot-cms-uve-image.component.html',
  styleUrls: ['./dot-cms-uve-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, NgClass, NgStyle, AsyncPipe, NgTemplateOutlet, DotCmsCodecEncodePipe, DotCmsAnimationComponent],
})
export class DotCmsUveImageComponent implements OnDestroy {
  content = input<BlockEditorContent>();
  attrs = input<BlockEditorNode['attrs']>();
  contentlet = input<ImageContentlet>();

  private readonly dotCmsUveFacade = inject(DotCmsUveFacade);
  private readonly destroy$ = new Subject<void>();

  readonly hosts = signal<string>('');
  readonly host$: Observable<string> = this.dotCmsUveFacade.getDotCmsHost$;
  readonly $srcURL = computed(() => {
    const host = this.hosts();
    //@ts-ignore
    const src = this.content()?.attrs?.src;
    return src ? `${host}${src}` : '';
  });

  getFieldAnimation = getFieldAnimation;
  isElementCreated = true;
  triggerCount = 1;

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildImageSrc(): Observable<string> {
    return combineLatest([this.host$.pipe(whenTruthy())]).pipe(
      whenTruthy(),
      map(([host]) => {
        this.hosts.set(host);

        const fileAsset = this.contentlet()?.fileAsset;
        const idPath = fileAsset?.idPath ?? '';

        if (idPath.startsWith('/dA/')) {
          return `${host}${idPath}`;
        }

        const pathParts = idPath.split('/');
        if (this.contentlet()) {
          const relativePath = pathParts.slice(6).join('/');
          return `${host}/dA/${this.contentlet().inode}/${relativePath}`;
        }

        return `${host}${idPath}`;
      })
    );
  }

  resetImageComponentStructure(identifier: string) {
    const imageContainer = document.getElementById(`image-container-${identifier}`);
    const imageElement = document.getElementById(`image-${identifier}`);

    if (imageElement?.querySelectorAll('.image-1')) {
      imageElement.removeAttribute('style');
      imageElement.classList.remove('image-1');
    }

    if (imageContainer && imageElement) {
      while (imageContainer.firstChild) {
        imageContainer.removeChild(imageContainer.firstChild);
      }
      imageContainer.appendChild(imageElement);
    }

    this.isElementCreated = true;
  }
}
